import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card, Button, Space, Tag, Typography, Empty, message, Divider,
  Drawer, List, Badge,
} from "antd";
import {
  fetchKitchenQueueDelta, pushOrderStatus, QueueItem, resetKitchenSince,
} from "../services/posQueue";
import { getDeviceSettings } from "../services/deviceSettings";
import { getRecent, clearHistory, appendReadyHistory } from "../services/kitchenHistory";
import { logoutToPin } from "../services/session";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "../utils/tauri";
import { ClockCircleFilled, WarningFilled } from "@ant-design/icons";
import { OrderTicketItem, printKitchenReadySlipStyled } from "../pos/print";
import { playKitchenDing, unlockAudio } from "../utils/sound"; // ⬅️ DODANO

const { Text, Title } = Typography;

/* -------- local prepared + history sizes -------- */
const LS_PREPARED_ITEMS = "pos.kitchen.prepared";
const READY_HISTORY_LEN = 200;

/* ---------------- Types ---------------- */
type PreparedMap = Record<string, number[]>;
type UndoEntry = {
  id: number;
  number: string;
  table?: string | number | null;
  created_at?: string;
  items: QueueItem["items"];
  note?: string;
  fromStatus: "ready";
  at: number;
};

/* ---------------- Prepared map helpers ---------------- */
function loadPreparedMap(key: string): PreparedMap {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}
function savePreparedMap(key: string, map: PreparedMap) {
  try { localStorage.setItem(key, JSON.stringify(map)); } catch {}
}
function getOrderPreparedSet(map: PreparedMap, orderId: number): Set<number> {
  const arr = map[String(orderId)] || [];
  return new Set(arr.filter((n) => Number.isFinite(n)));
}
function togglePrepared(map: PreparedMap, orderId: number, itemId: number): PreparedMap {
  const set = getOrderPreparedSet(map, orderId);
  if (set.has(itemId)) set.delete(itemId); else set.add(itemId);
  return { ...map, [String(orderId)]: Array.from(set.values()) };
}
 
/* ---------------- Time helpers ---------------- */
function parseToMs(s?: string): number | null { if (!s) return null; const t = Date.parse(s); return Number.isFinite(t) ? t : null; }
function mmss(ms: number): string { const sec = Math.max(0, Math.floor(ms/1000)); const m=Math.floor(sec/60), s=sec%60; return `${m}:${s<10?`0${s}`:s}`; }

/* ---------------------------------------------------- */

export default function KitchenQueue() {
  const [theme] = useState<string>(() => localStorage.getItem("pos.theme") || "light");
  useEffect(() => {
    localStorage.setItem("pos.theme", theme);
    const root = document.documentElement;
    if (theme === "dark") { root.classList.add("theme-dark"); root.classList.remove("theme-light"); }
    else { root.classList.add("theme-light"); root.classList.remove("theme-dark"); }
  }, [theme]);

  const [rows, setRows] = useState<QueueItem[]>([]);
  const [intervalMs, setIntervalMs] = useState(15000);
  const [lastSync, setLastSync] = useState<number | null>(null);

  // layout
  const [prefCols, setPrefCols] = useState<number>(4);
  const [cardW, setCardW] = useState<number>(320);

  // history (Spremno)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recent, setRecent] = useState(getRecent(READY_HISTORY_LEN));

  // UNDO drawer
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoList, setUndoList] = useState<UndoEntry[]>([]);

  // Etikete
  const labelsRef = useRef({
    title: "Kuhinja — narudžbe za pripremu",
    table: "Sto",
    take: "Preuzmi",
    ready: "Spremno",
    undo: "Vrati u pripremu",
    undoTitle: "Undo (spremno ▶ kuhinja)",
  });

  // zvuk
  const soundRef = useRef({ enabled: false, kind: "chime", volume: 0.8 });

  // poznati id-evi
  const knownIds = useRef<Set<number>>(new Set());

  // prepared toggles
  const [preparedMap, setPreparedMap] = useState<PreparedMap>(() => loadPreparedMap(LS_PREPARED_ITEMS));
  useEffect(() => savePreparedMap(LS_PREPARED_ITEMS, preparedMap), [preparedMap]);

  // settings
  useEffect(() => {
    (async () => {
      const s = await getDeviceSettings([
        "auto_sync_sec",
        "kitchen_max_cols", "kitchen_card_width",
        "kitchen_sound_enabled", "kitchen_sound_kind", "kitchen_sound_volume",
        "label_kitchen_title", "label_table", "label_take", "label_ready",
        "label_kitchen_undo", "label_kitchen_undo_title",
      ]);
      const sec = Number(s["auto_sync_sec"] ?? 15);
      setIntervalMs(Math.max(4000, sec * 1000));
      setPrefCols(clampInt(s["kitchen_max_cols"], 4, 1, 8));
      setCardW(clampInt(s["kitchen_card_width"], 320, 260, 520));

      labelsRef.current = {
        title: s["label_kitchen_title"] || labelsRef.current.title,
        table: s["label_table"] || labelsRef.current.table,
        take: s["label_take"] || labelsRef.current.take,
        ready: s["label_ready"] || labelsRef.current.ready,
        undo: s["label_kitchen_undo"] || labelsRef.current.undo,
        undoTitle: s["label_kitchen_undo_title"] || labelsRef.current.undoTitle,
      };

      soundRef.current = {
        enabled: !!s["kitchen_sound_enabled"],
        kind: (s["kitchen_sound_kind"] as string) || "chime",
        volume: clampInt(s["kitchen_sound_volume"], 80, 0, 100) / 100,
      };
    })();
  }, []);

  // ⬇️ Jednokratno otključavanje audio playback-a nakon prvog korisničkog dodira (browseri)
  useEffect(() => {
    const onAny = () => unlockAudio();
    window.addEventListener("pointerdown", onAny, { once: true });
    return () => window.removeEventListener("pointerdown", onAny);
  }, []);

  // columns by width
  const [cols, setCols] = useState<number>(calcColsFromWidth(cardW, 4));
  useEffect(() => {
    const onResize = () => setCols(calcColsFromWidth(cardW, prefCols));
    setCols(calcColsFromWidth(cardW, prefCols));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [cardW, prefCols]);

  // polling — VAŽNO: nema preparedMap u dependency-ju!
  useEffect(() => {
    let stop = false;

    async function tick() {
      try {
        const { rows: delta, serverTime } = await fetchKitchenQueueDelta(true, ["in_preparation"]);
        //filter only in_preparation
        const inPreparation = delta.filter((d) => d.status === "in_preparation");
        if (inPreparation.length) {
          const newOnes = inPreparation.filter((d) => !knownIds.current.has(d.id) && (d.items?.length ?? 0) > 0);
          setRows((prev) => {
            const merged = mergeOrders(prev, delta);
            knownIds.current = new Set(merged.map((o) => o.id));
            return merged;
          });
          if (newOnes.length && soundRef.current.enabled) {
            // 🔔 pokušaj Tauri/oscilator chime, pa fallback na WAV “ding”
            try {
              await playChime(soundRef.current.kind, soundRef.current.volume);
            } catch {
              await playKitchenDing();
            }
          }
        }
        setLastSync(serverTime);
      } catch {
        resetKitchenSince(); // force full next time
      }
    }

    (async () => {
      try {
        const init = await fetchKitchenQueueDelta(true, ["in_preparation"]);
        const inPreparation = init.rows.filter((d) => d.status === "in_preparation");
        if (inPreparation.length) {
          setRows(inPreparation);
          knownIds.current = new Set(inPreparation.map((o) => o.id));
        }
        setLastSync(init.serverTime);
      } catch {
        resetKitchenSince();
      }

      while (!stop) {
        await sleep(intervalMs);
        if (stop) break;
        await tick();
      }
    })();

    return () => { stop = true; };
  }, [intervalMs]); // <— samo interval, NE preparedMap

  /* ---------------- Actions ---------------- */
  const onTake = async (o: QueueItem) => {
    try {
      await pushOrderStatus(o.id, "in_preparation");
      setRows((prev) => prev.map((p) => (p.id === o.id ? { ...p, status: "in_preparation" } : p)));
    } catch { message.error("Nisam uspio označiti kao preuzeto."); }
  };

const onReady = async (o: QueueItem) => {
  // ŠTAMPA: styled "READY" slip preko centralnog printer modula
  try {
    await pushOrderStatus(o.id, "ready");

    appendReadyHistory({
      id: o.id, number: o.number, table: o.table,
      items: o.items.map((it) => ({ name: it.name, qty: it.qty, note: (it as any).note, modifiers: it.modifiers })),
      at: Math.floor(Date.now() / 1000),
    });
    setRecent(getRecent(READY_HISTORY_LEN));

    // ŠTAMPA: simple "READY" slip sa velikim brojem narudžbe
 
    // ubaci u UNDO i ukloni iz grida
    setUndoList((prev) => [
      { id: o.id, number: o.number, table: o.table, created_at: o.created_at, items: o.items, fromStatus: "ready" as const, at: Math.floor(Date.now()/1000) },
      ...prev,
    ].slice(0, 200));
    setRows((prev) => prev.filter((p) => p.id !== o.id));
    // setUndoOpen(true);
  } catch {
    message.error("Nisam uspio označiti kao spremno.");
  }
try {
  if (isTauri()) {
    const s = await getDeviceSettings(["printer_ip", "printer_share"]);
    const printerIp = String(s["printer_ip"] || "").trim() || undefined;
    const printerShare = String(s["printer_share"] || "").trim() || undefined;

    if (printerIp || printerShare) {
      const ticketItems: OrderTicketItem[] = (o.items || []).map((it: any) => ({
        name: it.name,
        qty: Number(it.qty || 1),
        modifiers: Array.isArray(it.modifiers) ? it.modifiers : [],
        note: (it as any).note ?? null,
      }));

      await printKitchenReadySlipStyled(
        {
          orderId: o.number,
          tableLabel: o.table ?? null,
          title: "SPREMNO",
        },
        ticketItems,
        { printerIp, printerShare }
      );
    }
  }
} catch (e) {
  console.warn("Kitchen ready print failed:", e);
}
};


  const onUndoReady = async (entry: UndoEntry) => {
    try {
      await pushOrderStatus(entry.id, "in_preparation");
      setRows((prev) => {
        const back: QueueItem = {
          id: entry.id, number: entry.number, status: "in_preparation",
          created_at: entry.created_at, items: entry.items, table: entry.table ?? null,
        };
        return mergeOrders([back, ...prev], []);
      });
      setUndoList((prev) => prev.filter((u) => u.id !== entry.id));
      message.success("Vraćeno u pripremu.");
    } catch { message.error("Nisam uspio vratiti status."); }
  };
  //update elapsed time 

  useEffect(() => {
    const interval = setInterval(() => {
      setRows((prev) => prev.map((o) => {
        const createdMs = parseToMs(o.created_at);
        const elapsed = createdMs ? mmss(Date.now() - createdMs) : null;
        return { ...o, elapsed };
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  /* ---------------- Header ---------------- */
  const header = useMemo(() => {
    const timeStr = lastSync
      ? new Date(lastSync * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "—";
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:12 }}>
        <Title level={4} style={{ margin: 0, color: theme === "dark" ? "#ffffff" : "#fff" }}>
          {labelsRef.current.title}
        </Title>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Text type="secondary" style={{ opacity: 0.75, color: theme === "dark" ? "#ffffff" : "#fff" }}>
            delta: {Math.round(intervalMs/1000)}s • zadnje: {timeStr}
          </Text>
          <Badge count={undoList.length} size="small">
            <Button onClick={() => setUndoOpen(true)}>Undo</Button>
          </Badge>
          <Button
            onClick={() => { setRecent(getRecent(READY_HISTORY_LEN)); setHistoryOpen(true); }}
          >
            Istorija
          </Button>
          <Button danger onClick={logoutToPin}>Odjava</Button>
        </div>
      </div>
    );
  }, [intervalMs, lastSync, theme, undoList.length]);

  if (!rows.length) {
    return (
      <div style={{ padding: 16, color: theme === "dark" ? "#ffffff" : "#000000" }}>
        {header}
        <div style={{ marginTop: 48, backgroundColor: theme === "dark" ? "#80c09cff" : "#fafafa", borderRadius: 18, padding: 24 }}>
          <Empty description="Nema narudžbi za pripremu" />
        </div>
        <UndoDrawer open={undoOpen} onClose={() => setUndoOpen(false)} list={undoList} labels={labelsRef.current} onUndo={onUndoReady} />
        <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} recent={recent} labels={labelsRef.current} cardW={cardW} />
      </div>
    );
  }



  return (
    <div style={{ padding: 16 }}>
      {header}
      <div style={{ columnCount: cols, columnGap: 12 }}>
        {rows.map((o) => {
          const createdMs = parseToMs(o.created_at);
          const elapsed = createdMs ? mmss(Date.now() - createdMs) : null;
          const prepSet = getOrderPreparedSet(preparedMap, o.id);

          return (
            <Card
              key={o.id}
              size="small"
              style={{ display:"inline-block", width:"100%", margin:"0 0 12px", breakInside:"avoid", borderRadius:14 }}
              title={
                <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
                  <Text strong style={{ fontSize:26, color: theme === "dark" ? "#ffffff" : "#000000" }}>#{o.number}</Text>
                  {//samo tece ce biti u pills obliku crvenom na desnom gornjem cosku malo veci font
                  <Tag color="red" style={{position: "absolute", top:-10, right:-10, fontWeight: 700, fontSize: 18,zIndex:5, padding: "0 6px" }} title={createdMs ? `kreirano ${new Date(createdMs).toLocaleString()}` : undefined}>
                    {elapsed}
                  </Tag>
                  }
                  
                  {o.table ? (
                    <Tag style={{
                      borderColor: theme === "dark" ? "#ffffff" : "#000000",
                      color: theme === "dark" ? "#ffffff" : "#000000",
                      background: theme === "dark" ? "#000000" : "#ffffff",
                    }}>
                      {labelsRef.current.table} {o.table}
                    </Tag>
                  ) : null}
                  {createdMs && (
                    <Text type="secondary" style={{ fontSize: 12, opacity:.85 }}>
                      <ClockCircleFilled />: {new Date(createdMs).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}
                    </Text>
                  )}
                </div>
              }
              extra={<Tag color={statusColor(o.status)} style={{ fontWeight: 500 }}>{prettyStatus(o.status)}</Tag>}
              styles={{ body: { paddingTop: 8, paddingBottom: 8 } }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  {o.items.map((it, idx) => {
                    const iid = Number(it.id ?? idx);
                    const isPrepared = prepSet.has(iid);
                    return (
                      <div key={iid} style={{ marginBottom: 10 }}>
                        {/* Lokalni toggle: ne utiče na feed; NE dira rows */}
                        <div
                          onClick={() => setPreparedMap((m) => togglePrepared(m, o.id, iid))}
                          style={{
                            cursor: "pointer",
                            padding: "4px 6px",
                            borderRadius: 8,
                            background: isPrepared ? (theme === "dark" ? "rgba(255,255,255,0.06)" : "#f3f3f3") : "transparent",
                            transition: "background 0.15s",
                          }}
                          title={isPrepared ? "Klikni da označiš kao 'nije gotovo'" : "Klikni da označiš kao 'pripremljeno'"}
                        >
                          <div style={{ display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline" }}>
                            <Text strong style={{
                              fontSize:16, color: isPrepared ? "red" : (theme === "dark" ? "#ffffff" : "#000000"),
                              opacity: isPrepared ? 0.5 : 1,  textDecoration: isPrepared ? "line-through" : "none",
                            }}>
                              {it.qty}× {it.name} {
                                o.note && (
                                  <Text type="secondary" style={{ fontSize: 12, opacity:.85, color: theme === "dark" ? "red" : "red" }}>
                                    <WarningFilled />: {o.note}
                                  </Text>
                                )
                              }
                            </Text>
                          </div>

                          {it.modifiers?.length ? (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:6, margin:"4px 0" }}>
                              {it.modifiers.map((m, i) => (
                                <Tag key={i} style={{
                                  borderColor: theme === "dark" ? "#ffffff" : "#000000",
                                  color: theme === "dark" ? "#ffffff" : "#000000",
                                  background: theme === "dark" ? "#000000" : "#ffffff",
                                  fontSize: 12, opacity: isPrepared ? 0.5 : 1,
                                }}>
                                  {m}
                                </Tag>
                              ))}
                            </div>
                          ) : null}

                          {(it as any).note ? (
                            <div style={{
                              marginTop: 4, padding: "6px 8px", borderRadius: 8,
                              background: theme === "dark" ? "#ffffff" : "#000000",
                              border: `1px solid ${theme === "dark" ? "#ffffff" : "#000000"}`,
                              opacity: isPrepared ? 0.5 : 1,
                            }}>
                              <Text style={{ fontStyle:"italic", color: theme === "dark" ? "#000000" : "#ffffff" }}>
                                {(it as any).note}
                              </Text>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Divider style={{ margin: "6px 0" }} />

                <Space>
                  {o.status !== "in_preparation" && o.status !== "ready" && (
                    <Button type="primary" onClick={() => onTake(o)}>{labelsRef.current.take}</Button>
                  )}
                  <Button onClick={() => onReady(o)}>{labelsRef.current.ready}</Button>
                </Space>
              </Space>
            </Card>
          );
        })}
      </div>

      {/* Drawers */}
      <UndoDrawer open={undoOpen} onClose={() => setUndoOpen(false)} list={undoList} labels={labelsRef.current} onUndo={onUndoReady} />
      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} recent={recent} labels={labelsRef.current} cardW={cardW} />
    </div>
  );
}

/* ---------------- Drawers ---------------- */

function UndoDrawer({
  open, onClose, list, labels, onUndo,
}: {
  open: boolean;
  onClose: () => void;
  list: UndoEntry[];
  labels: { undoTitle: string; undo: string; table: string; };
  onUndo: (u: UndoEntry) => void;
}) {
  return (
    <Drawer open={open} width={540} title={labels.undoTitle} onClose={onClose}>
      {!list.length ? <Empty description="Nema stavki za Undo" /> : (
        <List
          dataSource={list}
          renderItem={(u) => (
            <List.Item key={`${u.id}-${u.at}`} actions={[<Button danger onClick={() => onUndo(u)}>{labels.undo}</Button>]}>
              <div style={{ width: "100%" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <Text strong>#{u.number}</Text>
                  <Text type="secondary">{new Date(u.at*1000).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}</Text>
                </div>
                {u.table ? (
                  <div style={{ marginTop: 4, marginBottom: 8 }}>
                    <Tag>{labels.table} {u.table}</Tag>
                  </div>
                ) : null}
                <div>
                  {u.items.map((it, i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      {it.modifiers?.length ? (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:4 }}>
                          {it.modifiers.map((m, j) => <Tag key={j}>{m}</Tag>)}
                        </div>
                      ) : null}
                      <Text strong>{it.qty}× {it.name}</Text>
                      {(it as any).note ? (
                        <div style={{ marginTop: 2, padding: "4px 6px", borderRadius: 6, border:"1px solid #ddd" }}>
                          <Text style={{ fontStyle:"italic" }}>{(it as any).note}</Text>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
}

function HistoryDrawer({
  open, onClose, recent, labels, cardW,
}: {
  open: boolean; onClose: () => void;
  recent: ReturnType<typeof getRecent>;
  labels: any; cardW: number;
}) {
  return (
    <Drawer
      open={open}
      width={Math.max(420, cardW + 120)}
      title={`Zadnjih ${READY_HISTORY_LEN} — označeno kao Spremno`}
      onClose={onClose}
      extra={<Button onClick={() => { clearHistory(); }}>{`Očisti`}</Button>}
    >
      {!recent || recent.length === 0 ? (
        <Empty description="Nema istorije" />
      ) : (
        <List
          dataSource={recent}
          renderItem={(h) => (
            <List.Item key={`${h.id}-${h.at}`}>
              <div style={{ width: "100%" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <Text strong>#{h.number}</Text>
                  <Text type="secondary">
                    {new Date(h.at*1000).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}
                  </Text>
                </div>
                <div style={{ marginTop: 4, marginBottom: 8 }}>
                  {h.table ? <Tag>{labels.table} {h.table}</Tag> : null}
                </div>
                <div>
                  {h.items.map((it, i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      {it.modifiers?.length ? (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:4 }}>
                          {it.modifiers.map((m: string, j: number) => <Tag key={j}>{m}</Tag>)}
                        </div>
                      ) : null}
                      <Text strong>{it.qty}× {it.name}</Text>
                      {(it as any).note ? (
                        <div style={{ marginTop: 2, padding: "4px 6px", borderRadius: 6, border:"1px solid #ddd" }}>
                          <Text style={{ fontStyle:"italic" }}>{(it as any).note}</Text>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
                <Divider style={{ margin: "10px 0" }} />
              </div>
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
}

/* ---------------- Helpers ---------------- */

function sleep(ms: number) { return new Promise((r) => setTimeout(r, ms)); }
function clampInt(v: any, d: number, min: number, max: number) {
  const n = Number(v); return Number.isFinite(n) ? Math.max(min, Math.min(max, Math.round(n))) : d;
}
function statusColor(status?: string) {
  switch (status) {
    case "in_preparation": return "orange";
    case "ready": return "green";
    case "delivered":
    case "paid": return "purple";
    default: return "blue";
  }
}
function prettyStatus(s?: string) { if (!s) return ""; if (s==="in_preparation") return "u pripremi"; return s; }
function calcColsFromWidth(cardW: number, prefCols: number) {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  const maxByWidth = Math.max(1, Math.min(8, Math.floor(w/(cardW+20))));
  return Math.min(maxByWidth, Math.max(1, Math.min(8, prefCols)));
}
async function playChime(kind: string, volume: number) {
  if (isTauri()) { try { await invoke("play_kitchen_sound", { kind, volume }); return; } catch {} }
  try {
    const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new Ctx(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.type="sine"; o.frequency.setValueAtTime(900, ctx.currentTime); g.gain.value = volume;
    o.connect(g).connect(ctx.destination); o.start();
    setTimeout(()=>{ o.frequency.setValueAtTime(1200, ctx.currentTime); }, 220);
    setTimeout(()=>{ o.stop(); ctx.close(); }, 480);
  } catch {
    // ako oscilator ne uspije (autoplay policy), prepusti WAV fallback-u iz playKitchenDing
    throw new Error("Oscillator failed");
  }
}
function mergeOrders(prev: QueueItem[], delta: QueueItem[]): QueueItem[] {
  const byId = new Map<number, QueueItem>(prev.map((o) => [o.id, o]));
  for (const d of delta) {
    if (!d.items || d.items.length === 0) { byId.delete(d.id); continue; }
    const old = byId.get(d.id);
    if (!old) byId.set(d.id, d);
    else byId.set(d.id, { ...old, ...d, items: d.items, status: d.status ?? old.status, created_at: d.created_at ?? old.created_at, ts: d.ts ?? old.ts });
  }
  const toTime = (s?: string) => (s ? Date.parse(s) || 0 : 0);
  return Array.from(byId.values()).sort((a,b)=>{ const ta=toTime(a.created_at), tb=toTime(b.created_at); return ta!==tb? ta-tb : a.id-b.id; });
}
